/**
 * 交易模块：创建订单 / 列表 / 详情 / 支付 / 交割 / 取消 / 交易大厅聚合
 * 联调对齐（M2-2 遗留 #4）：支付为 PUT、列表按 buyer_id/seller_id 过滤、
 * 新增资金托管（/escrow）、电子合同（/contracts）、评价（/reviews）对接。
 */
import { API_BASE, ApiError, TOKEN_KEY, get, post, put } from './http';
import { canFallbackToMock } from './errors';
import {
  mockApplyArbitration,
  mockCancelOrder,
  mockCompleteOrder,
  mockCreateOrder,
  mockGetOrder,
  mockHall,
  mockListOrders,
  mockPayOrder,
  mockShipOrder,
  USE_MOCK,
} from './mock';
import { CATEGORIES } from '@/utils/constants';
import type {
  ArbitrationParams,
  CompleteOrderParams,
  ContractRecord,
  CreateOrderParams,
  CreateOrderResult,
  EscrowStatistics,
  EscrowTransaction,
  EscrowTransactionQuery,
  HallData,
  Paginated,
  ReviewItem,
  TradeOrder,
} from '@/types';

/** 创建订单（下单锁牛 15 分钟 IR-05；type=3 拍卖单可携带 auction_id/deal_price） */
export async function createOrder(params: CreateOrderParams): Promise<CreateOrderResult> {
  try {
    return await post<CreateOrderResult>('/orders', params);
  } catch (e) {
    if (canFallbackToMock(e)) return mockCreateOrder(params);
    throw e;
  }
}

/**
 * 我的订单列表（视角：buy 我买到的 / sell 我卖出的）
 * 后端 OrderListQuery 无 view 字段，按 buyer_id / seller_id 过滤（联调对齐）
 */
export async function listOrders(view: 'buy' | 'sell', userId: number): Promise<TradeOrder[]> {
  try {
    const data = await get<{ list: TradeOrder[] }>('/orders', {
      page: 1,
      page_size: 100,
      ...(view === 'buy' ? { buyer_id: userId } : { seller_id: userId }),
    });
    return data.list ?? [];
  } catch (e) {
    if (canFallbackToMock(e)) return mockListOrders();
    throw e;
  }
}

/** 订单详情 */
export async function getOrder(id: number): Promise<TradeOrder> {
  try {
    return await get<TradeOrder>(`/orders/${id}`);
  } catch (e) {
    if (canFallbackToMock(e)) return mockGetOrder(id);
    throw e;
  }
}

/**
 * 确认支付（资金 2FA：必传支付密码）
 * 后端为 PUT /orders/:id/pay，body { payment_password }（binding required,min=6,max=20）。
 * 校验失败时后端以 HTTP 403 + body.code 透传：2004 未设置 / 2006 错误 / 2007 锁定（N3）。
 */
export async function payOrder(id: number, paymentPassword?: string): Promise<{ ok: boolean }> {
  try {
    return await put<{ ok: boolean }>(
      `/orders/${id}/pay`,
      paymentPassword ? { payment_password: paymentPassword } : undefined,
    );
  } catch (e) {
    if (canFallbackToMock(e)) return mockPayOrder(id);
    throw e;
  }
}

/** 卖家安排交割（已付款 → 已发货） */
export async function shipOrder(id: number): Promise<{ ok: boolean }> {
  try {
    return await put<{ ok: boolean }>(`/orders/${id}/ship`);
  } catch (e) {
    if (canFallbackToMock(e)) return mockShipOrder(id);
    throw e;
  }
}

/** 确认交割（≥3 张凭证 + GPS + 备注） */
export async function completeOrder(id: number, params: CompleteOrderParams): Promise<{ ok: boolean }> {
  try {
    return await put<{ ok: boolean }>(`/orders/${id}/complete`, params);
  } catch (e) {
    if (canFallbackToMock(e)) return mockCompleteOrder(id, params.vouchers);
    throw e;
  }
}

/**
 * 下载合同文本（A-05，.txt 非 PDF）。
 * 该接口返回 text/plain 二进制而非 JSON envelope，故不走 http.ts 通用解包：
 * 直接 fetch 携带 JWT → blob → 触发浏览器下载（文件名取 Content-Disposition）。
 */
export async function downloadContractText(orderId: number): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API_BASE}/contracts/orders/${orderId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let message = `下载失败（HTTP ${res.status}）`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* 非 JSON 响应，用默认文案 */
    }
    throw new ApiError(res.status, message, res.status);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ?? `contract_${orderId}.txt`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** 取消订单（待付款时） */
export async function cancelOrder(id: number, reason?: string): Promise<{ ok: boolean }> {
  try {
    return await put<{ ok: boolean }>(`/orders/${id}/cancel`, { reason });
  } catch (e) {
    if (canFallbackToMock(e)) return mockCancelOrder(id, reason);
    throw e;
  }
}

// ========== P0-5 订单状态机补全（结算 / 售后，后端 handler/trade.go 2026-08-20）==========
// 路由（router.go）：PUT /orders/:id/settle、PUT /orders/:id/after-sales/open|close
// 权限语义以代码为准：settle 仅平台管理员（service 层 orderActionSettle 拦截）；
// after-sales open/close 仅买家本人或平台管理员。售后退款资金链路后端尚未打通（N8 遗留），
// 前端仅展示/调用已存在的接口，不虚构退款动作。

/** 订单结算（仅平台管理员，trade 端前端不主动调用；admin 端使用） */
export async function settleOrder(id: number): Promise<{ ok: boolean }> {
  return put<{ ok: boolean }>(`/orders/${id}/settle`);
}

/** 开启售后（买家：已完成 3 → 售后中 7，body 必带 reason 5-512 字） */
export async function openAfterSales(id: number, reason: string): Promise<{ ok: boolean }> {
  return put<{ ok: boolean }>(`/orders/${id}/after-sales/open`, { reason });
}

/** 关闭售后（售后中 7 → 已完成 3；买家撤回或平台处理完成） */
export async function closeAfterSales(id: number): Promise<{ ok: boolean }> {
  return put<{ ok: boolean }>(`/orders/${id}/after-sales/close`);
}

/** 申请仲裁（后端未实现该接口，保留兼容；页面已不引导使用） */
export async function applyArbitration(id: number, params: ArbitrationParams): Promise<{ ok: boolean }> {
  try {
    return await post<{ ok: boolean }>(`/orders/${id}/arbitration`, params);
  } catch (e) {
    if (canFallbackToMock(e)) return mockApplyArbitration(id);
    throw e;
  }
}

/**
 * 交易大厅聚合数据（品类分区 + 精选 + 成交流 + 公告）
 * ★ 架构 §9-3：/trade/hall 已接真实后端，仅网络/404 降级 mock。
 *   后端 HallData 字段与前端展示契约存在差异（categories 无 key/desc、featured 为精简牛只、
 *   recent_trades 用 order_id 且 time 可空），此处做一层适配映射。
 */

// ===== /trade/hall 后端响应契约（与前端 HallData 略有差异）=====
interface HallCategoryRaw {
  name: string;
  count: number;
}
interface HallFeaturedRaw {
  id: number;
  breed: string;
  weight: number;
  gender: number;
  face_image_url: string;
  farm_name: string;
  price: number;
}
interface HallTradeRaw {
  order_id: number;
  region: string;
  breed: string;
  quantity: number;
  price: number;
  time: string | null;
}
interface HallNoticeRaw {
  id: number;
  title: string;
  content: string;
  created_at: string;
}
interface HallDataRaw {
  categories: HallCategoryRaw[];
  featured: HallFeaturedRaw[];
  recent_trades: HallTradeRaw[];
  notices: HallNoticeRaw[];
  total_on_sale: number;
}

/** 将后端 /trade/hall 响应适配为前端 HallData 展示契约 */
function normalizeHall(raw: HallDataRaw): HallData {
  return {
    categories: raw.categories.map((c) => ({
      key: c.name,
      name: c.name,
      desc: CATEGORIES.find((x) => x.name === c.name)?.desc ?? c.name,
      count: c.count,
    })),
    featured: raw.featured.map((f) => ({
      id: f.id,
      ear_tag_no: '',
      breed: f.breed,
      weight: f.weight,
      gender: f.gender,
      face_image_url: f.face_image_url,
      images: f.face_image_url ? [f.face_image_url] : [],
      farm_name: f.farm_name,
      listing_price: f.price,
      sale_status: 0,
      trade_mode: 1,
      available_qty: 1,
    })),
    recent_trades: raw.recent_trades.map((t) => ({
      id: t.order_id,
      region: t.region,
      breed: t.breed,
      quantity: t.quantity,
      price: t.price,
      time: t.time ?? '',
    })),
    notices: raw.notices.map((n) => ({
      id: n.id,
      title: n.title,
      content: n.content,
      created_at: n.created_at,
    })),
    total_on_sale: raw.total_on_sale,
  };
}

export async function getTradeHall(): Promise<HallData> {
  if (USE_MOCK.hall) return mockHall;
  try {
    const raw = await get<HallDataRaw>('/trade/hall');
    return normalizeHall(raw);
  } catch (e) {
    if (canFallbackToMock(e)) return mockHall;
    throw e;
  }
}

// ========== 资金托管（M2-2 联调遗留 #4）==========

/** 订单托管统计（金额单位为分） */
export async function getEscrowOverview(orderId: number): Promise<EscrowStatistics> {
  return get<EscrowStatistics>(`/escrow/orders/${orderId}`);
}

/** 账务流水分页查询 */
export async function listEscrowTransactions(params: EscrowTransactionQuery): Promise<Paginated<EscrowTransaction>> {
  return get<Paginated<EscrowTransaction>>('/escrow/transactions', {
    page: params.page,
    page_size: params.page_size,
    order_id: params.order_id,
    user_id: params.user_id,
    trans_type: params.trans_type,
  });
}

// ========== 电子合同（M2-2 联调遗留 #4）==========

/** 按订单查询最新合同（404 时由调用方降级为订单数据预览） */
export async function getContractByOrder(orderId: number): Promise<ContractRecord> {
  return get<ContractRecord>(`/contracts/orders/${orderId}`);
}

// ========== 评价（M2-2 联调遗留 #4）==========

export interface CreateReviewParams {
  order_id: number;
  role: number; // 1-买家评卖家 2-卖家评买家
  rating: number; // 1-5
  content?: string;
}

/** 提交评价（一单一评，完成后 15 天内） */
export async function createReview(params: CreateReviewParams): Promise<ReviewItem> {
  return post<ReviewItem>('/reviews', params);
}

/** 我发出的评价 */
export async function listMyReviews(): Promise<ReviewItem[]> {
  const data = await get<{ list: ReviewItem[] }>('/reviews/my');
  return data.list ?? [];
}
